import type { AccessorKeyColumnDef, CellContext, ColumnDef } from '@tanstack/vue-table'
import { GroupColor, JSSchema, JSSchemaObject, JSSchemaObjectTypes } from '.'

import CalendarFormatted from 'pejer/src/components/CalendarFormatted.vue'
import CheckMarkDisplay from 'pejer/src/components/CheckMarkDisplay.vue'
import JsonViewer from 'pejer/src/components/JsonViewer.vue'
import NumberFormatted from 'pejer/src/components/NumberFormatted.vue'
import TextIconTableCell from 'pejer/src/components/TextIconTableCell.vue'
import { Badge } from 'pejer/src/components/ui/badge'
import { FunctionalComponent, h, RendererElement, RendererNode, VNode } from 'vue'
import PejerBase from '..'
import ComplianceFlag from '../components/ComplianceFlag.vue'
import EnumDisplay from '../components/EnumDisplay.vue'
import NorwayIcon from '../components/Icons/Countries/NorwayIcon.vue'
import MoneyFormatted from '../components/MoneyFormatted.vue'
import PhoneFormatted from '../components/PhoneFormatted.vue'
import RedYesGreenNoBooleanDisplay from '../components/RedYesGreenNoBooleanDisplay.vue'
import ClipboardCopy from '../components/Table/core/ClipboardCopy.vue'
import LimitedDisplay from '../components/Table/core/LimitedDisplay.vue'
import YellowYesGreenNoBooleanDisplay from '../components/YellowYesGreenNoBooleanDisplay.vue'
import { cn, stripUrl } from '../lib/utils'
import { LooseColumnDef } from '../types/base'
import { ConnectedAccountComplianceStatus } from './Global'
import { WebhookChannel } from './Webhook'

export const ColorPalette = {
  red: {
    primary: '#FF6961',
    accent: '#F38181',
  },
  pink: {
    primary: '#FFC0CB',
    accent: '#FF69B4',
  },
  orange: {
    primary: '#FFA07A',
    accent: '#FFA07A',
  },
  yellow: {
    primary: '#FFD700',
    accent: '#FCE38A',
  },
  blue: {
    primary: '#77DD77',
    accent: '#00CCC0',
  },
  green: {
    primary: '#77DD77',
    accent: '#4ab85b',
  },
  brown: {
    primary: '#D2691E',
    accent: '#D2691E',
  },
  purple: {
    primary: '#9370DB',
    accent: '#9370DB',
  },
  black: {
    primary: '#3f3f46',
    accent: '#3f3f46',
  },
} as const satisfies {
  [key: string]: GroupColor
}

export function fillWebhookChannels(webhookChannels: WebhookChannel[]) {
  const result: { [key: string]: Partial<WebhookChannel> } = {}

  // There is only one webhook channel per type
  webhookChannels.forEach((channel) => {
    result[channel.webhookType] = channel
  })

  // Fill in the missing webhook channels
  for (const key in webhookChannelEnum) {
    if (!(key in result)) {
      result[key] = {
        webhookType: key,
      }
    }
  }

  return result
}

export const textWithIconCell = (
  icon: FunctionalComponent<any, {}, any, {}>,
  text: VNode<RendererNode, RendererElement, { [key: string]: any }> | string
) => {
  return h(
    TextIconTableCell,
    {},
    {
      icon: () =>
        h(icon, {
          size: 16,
          class: 'shrink-0',
        }),
      default: () => text,
    }
  )
}

export const webhookChannelEnum = {
  // connectedaccount: 'connectedaccount',
  // partialpay: 'partialpay',
  // payout: 'payout',
  // payoutfailed: 'payoutfailed',
  catchAll: 'catchall',
} as const

export function mergeColumns<T>(column1: ColumnDef<T>[], column2: ColumnDef<T>[]): ColumnDef<T>[] {
  const map: Record<string, ColumnDef<T>[]> = {}
  const atEnd: Record<string, ColumnDef<T>[]> = {}

  const existingArray = new Map<string, boolean>()

  column1.forEach((col) => {
    const key = ('accessorKey' in col ? col.accessorKey : col.id) as string
    const group = col.meta?.group || 'Other'

    if (existingArray.has(key)) return
    existingArray.set(key, true)

    const mapToInput = col.meta?.placement === 'end' ? atEnd : map

    // If the column has a group, put it in the group
    if (!(group in mapToInput)) mapToInput[group] = []

    mapToInput[group].push(col)
  })
  column2.forEach((col) => {
    const key = ('accessorKey' in col ? col.accessorKey : col.id) as string
    const group = col.meta?.group || 'Other'

    if (existingArray.has(key)) return
    existingArray.set(key, true)

    const mapToInput = col.meta?.placement === 'end' ? atEnd : map

    // If the column has a group, put it in the group
    if (!(group in mapToInput)) mapToInput[group] = []

    mapToInput[group].push(col)
  })

  const result: ColumnDef<T>[] = []

  // Add the atEnd map values at the end
  Object.entries(atEnd).forEach(([group, cols]) => {
    if (!(group in map)) map[group] = []
    map[group].push(...cols)
  })

  Object.values(map).forEach((value) => {
    result.push(...value)
  })

  return result
}

export const DB_NULL_DATE = '0001-01-01T00:00:00'

export function dateHasPassed(date: Date): boolean {
  return date.getTime() < Date.now()
}

export function wrapCellInClipboardCopy(
  cell: () => VNode<RendererNode, RendererElement, { [key: string]: unknown }>,
  value: any,
  dontWrapInClipboardCopy?: boolean
) {
  if (dontWrapInClipboardCopy) return h(cell)
  return h(ClipboardCopy, { value }, () => h(cell, { value }))
}

function schemaObjectIsStringArray(schemaObj: JSSchemaObjectTypes, value: any): value is string[] {
  return schemaObj.type === 'string' && schemaObj.metaType === 'array' && Array.isArray(value)
}

function schemaObjectIsJsonObject(schemaObj: JSSchemaObjectTypes, value: any): value is object {
  return (
    schemaObj.type === 'string' &&
    schemaObj.metaType === 'json' &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    value !== null
  )
}

export function defaultCell(
  schemaObj: JSSchemaObjectTypes,
  value: string | number | boolean | string[] | null | undefined | object,
  full: Object,
  options?: CellContext<any, any>['options'],
  dontWrapInClipboardCopy: boolean = true
) {
  const sensitiveClass = schemaObj.sensitive ? 'sentry-mask' : ''

  if (schemaObj.type === 'number') {
    switch (schemaObj.metaType) {
      case 'money':
        return wrapCellInClipboardCopy(
          () =>
            h(MoneyFormatted, {
              amount: value as number,
              oneSize: true,
              currency: schemaObj.currencyColumn ? full[schemaObj.currencyColumn] : null,
              class: sensitiveClass,
            }),
          value,
          dontWrapInClipboardCopy
        )
    }
    return wrapCellInClipboardCopy(
      () => h(NumberFormatted, { value: value, class: sensitiveClass }),
      value,
      dontWrapInClipboardCopy
    )
  }

  if (schemaObj.type === 'boolean') {
    if (value === null || value === undefined) return ''
    switch (schemaObj.metaType) {
      case 'YellowYesGreenNo':
        return h(YellowYesGreenNoBooleanDisplay, {
          value: value as boolean,
        })

      case 'RedYesGreenNo':
        return h(RedYesGreenNoBooleanDisplay, {
          value: value as boolean,
        })

      default:
        return h(CheckMarkDisplay, { value: value as boolean })
    }
  }

  if (schemaObjectIsStringArray(schemaObj, value)) {
    return wrapCellInClipboardCopy(
      () =>
        h(
          LimitedDisplay,
          { content: value.join(', '), class: 'flex gap-1', oneLine: options?.oneLine },
          () => [
            value.map((val: string) =>
              h(
                Badge,
                {
                  size: 16,
                  class: 'shrink-0',
                  variant: 'outline',
                },
                () => val
              )
            ),
          ]
        ),
      value.join(', '),
      dontWrapInClipboardCopy
    )
  }

  if (schemaObjectIsJsonObject(schemaObj, value)) {
    const jsonString = JSON.stringify(value, null, 2)
    if (options?.oneLine)
      return wrapCellInClipboardCopy(
        () => h(LimitedDisplay, { content: jsonString, oneLine: true }),
        jsonString,
        dontWrapInClipboardCopy
      )

    return wrapCellInClipboardCopy(
      () =>
        h(JsonViewer, {
          value: value as object,
          sort: true,
          expandDepth: 3,
          class: 'w-full',
        }),
      jsonString,
      dontWrapInClipboardCopy
    )
  }

  if (schemaObj.type === 'date') {
    return wrapCellInClipboardCopy(
      () =>
        h(CalendarFormatted, {
          date: value === DB_NULL_DATE ? null : (value as string),
          isSensitive: schemaObj.sensitive,
          oneLine: options?.oneLine,
          simple: true,
          hideIcon: true,
          showSeconds: schemaObj.metaType === 'withSeconds',
        }),
      value === DB_NULL_DATE ? null : value,
      dontWrapInClipboardCopy
    )
  }

  // Only strings are supported onwards
  if (typeof value !== 'string')
    return wrapCellInClipboardCopy(
      () =>
        h(LimitedDisplay, {
          content: String(value ?? ''),
          class: sensitiveClass,
          oneLine: options?.oneLine,
        }),
      value,
      dontWrapInClipboardCopy
    )

  // Could show a popup of all possible values for enum when hovered, but for now just show the value
  if (schemaObj.type === 'enum') {
    if (
      schemaObj.metaType === 'flag' &&
      ConnectedAccountComplianceStatus.findIndex(() => value) !== -1
    ) {
      return h(ComplianceFlag, {
        size: 16,
        value: value as (typeof ConnectedAccountComplianceStatus)[number],
      })
    }

    return wrapCellInClipboardCopy(
      () => h(EnumDisplay, { text: value }),
      value,
      dontWrapInClipboardCopy
    )
  }

  if (schemaObj.type === 'string' && value) {
    switch (schemaObj.metaType) {
      case 'email':
        return wrapCellInClipboardCopy(
          () =>
            h(LimitedDisplay, { content: String(value), oneLine: options?.oneLine }, () =>
              h(
                'a',
                {
                  href: value ? 'mailto:' + value : '',
                  class: 'hover:underline w-full sentry-mask',
                },
                value //textWithIconCell(Mail, value)
              )
            ),
          value,
          dontWrapInClipboardCopy
        )

      case 'mutedText':
        return wrapCellInClipboardCopy(
          () =>
            h(
              'span',
              {
                class: cn(sensitiveClass, 'text-xs text-slate-500'),
              },
              value
            ),
          value,
          dontWrapInClipboardCopy
        )

      case 'phone':
        return wrapCellInClipboardCopy(
          () =>
            h(PhoneFormatted, {
              class: sensitiveClass,
              phoneNumber: value,
              prefix: schemaObj.prefixColumn ? full[schemaObj.prefixColumn] : null,
              simple: true,
            }),
          (schemaObj.prefixColumn ? full[schemaObj.prefixColumn] : '+47 ') + value,
          dontWrapInClipboardCopy
        )

      case 'countryCode':
        return wrapCellInClipboardCopy(
          () =>
            h(
              LimitedDisplay,
              {
                content: value,
                class: cn('flex items-center gap-2', sensitiveClass),
                oneLine: options?.oneLine,
              },
              () => (value?.toLowerCase() === 'no' ? h(NorwayIcon) : value)
            ),
          value,
          dontWrapInClipboardCopy
        )

      case 'url':
        return wrapCellInClipboardCopy(
          () =>
            h(LimitedDisplay, { content: String(value), oneLine: options?.oneLine }, () =>
              h(
                'a',
                {
                  href: value,
                  class: 'hover:underline w-full sentry-mask',
                  target: '_blank',
                },
                // textWithIconCell(
                //   Globe,
                h(
                  'span',
                  {
                    class: cn('whitespace-nowrap overflow-hidden text-ellipsis', sensitiveClass),
                    style: !value ? { color: 'gray', fontSize: '0.8rem' } : {},
                  },
                  [value ? stripUrl(value) : 'No URL']
                )
                // )
              )
            ),
          value,
          dontWrapInClipboardCopy
        )

      case 'badge':
        return wrapCellInClipboardCopy(
          () =>
            h(
              Badge,
              {
                variant: 'secondary',
                class: 'w-fit',
              },
              () => value
            ),
          value,
          dontWrapInClipboardCopy
        )
    }
  }

  return wrapCellInClipboardCopy(
    () => h(LimitedDisplay, { content: value, class: sensitiveClass, oneLine: options?.oneLine }),
    value,
    dontWrapInClipboardCopy
  )
}

export function fillColumnsWithDefaults<T>(
  pejer: PejerBase<any, any>,
  schema: JSSchema<T, any>,
  columns: LooseColumnDef<T>[]
) {
  const formattedColumns = columns.map((col) => {
    if (typeof col === 'string') col = { accessorKey: col }
    return col
  })
  formattedColumns.forEach((col) => {
    // Drop all of the columns that don't have the cell key set
    if (
      !('accessorKey' in col) ||
      'accessorFn' in col ||
      'id' in col ||
      col.accessorKey.toString() == 'actions'
    ) {
      return
    }
    // const originalSchema = schema[col.accessorKey] as JSSchemaObject
    const originalSchema = pejer.getSchemaObjectFromFlattenedKeys(
      schema as JSSchema,
      col.accessorKey as string
    )

    const schemaObj = pejer.getSchemaReferencedObject(originalSchema)
    if (!schemaObj) {
      console.error(
        new Error(
          `Column '${col.accessorKey.toString()}' was not found in the schema: ${schema.name}`
        )
      )
      return
    }

    const newMeta = {
      searchable: true,
      sortable: true,
      group: originalSchema?.meta?.group,
      forceDisplay: schemaObj.specificCell !== undefined || schemaObj.formattedCell !== undefined,
      ...schemaObj.meta,
      ...col.meta,
    } satisfies ColumnDef<any>['meta']

    col.meta = newMeta

    if (!col.size) col.size = schemaObj.meta?.size ?? cellWidthTypes[schemaObj.type] ?? undefined
    if (!col.header) col.header = schemaObj.humanReadable

    // if ('cell' in col) {
    //   const oldCell = col.cell

    //   col.cell = (props) => {
    //     return h('div', { class: schemaObj.sensitive ? 'sentry-mask' : '' },
    //       h(oldCell, props)
    //     )
    //   }
    //   return
    // }

    // We follow this order of precedence
    // 1. Column cell
    // 2. Specific cell of the schema object
    // 3. Formatted cell of the schema object
    // 4. Formatted cell of the related schema object
    // 5. Default cell
    if (originalSchema.specificCell) {
      col.cell = originalSchema.specificCell(originalSchema)
    } else if (originalSchema.formattedCell) {
      col.cell = ({ row }) =>
        originalSchema.formattedCell(getNestedObject(row.original, col.accessorKey as string))
    } else if (schemaObj.formattedCell) {
      col.cell = ({ row }) =>
        schemaObj.formattedCell(getNestedObject(row.original, col.accessorKey as string))
    } else {
      const defaultCells: AccessorKeyColumnDef<T>['cell'] = ({ row, options }) =>
        defaultCell(
          schemaObj,
          getNestedObject(row.original, col.accessorKey as string),
          row.original,
          options
        )

      col.cell = defaultCells
    }
  })

  const columnActionsIndex = formattedColumns.findIndex((col) => col.id === 'actions')
  if (schema.actions && columnActionsIndex !== -1) {
    const actions = {
      meta: {
        placement: 'end',
      },
      size: Object.keys(schema.actions).length * 30,
      cell: (props) =>
        h(
          'div',
          {
            class: 'flex justify-end w-fit self-end justify-self-end place-self-end',
          },
          // Might want different ordering in the future
          Object.values(schema.actions).map((component) => component.cell(props))
        ),
      ...formattedColumns[columnActionsIndex],
    } satisfies ColumnDef<T>
    formattedColumns[columnActionsIndex] = actions
  }
  return formattedColumns
}

const cellWidthTypes = {
  enum: 100,
  number: 50,
  string: 200,
}

export function generateColumns<T>(
  pejer: PejerBase<any, any>,
  schema: JSSchema<T>
): ColumnDef<T>[] {
  const flattenedSchema = pejer.flattenSchemaFields(schema)
  return Object.entries<JSSchemaObject>(flattenedSchema)
    .map(([key, value]) => {
      if ('type' in value && value.type === 'object') return null

      const schemaObj = pejer.getSchemaReferencedObject(value)

      const def = {
        accessorKey: key,
        header: schemaObj.humanReadable,
        meta: {
          sortable: true,
          hide_default: true,
          group: value.meta?.group,
          hideFromTable: value.meta?.hideFromTable,
          ...(schemaObj.meta || {}),
        },
        size: schemaObj.meta?.size ?? cellWidthTypes[schemaObj.type] ?? undefined,
        cell: undefined,
      } satisfies AccessorKeyColumnDef<T>

      // We follow this order of precedence
      // 1. Specific cell of the column
      // 2. Formatted cell of the schema object
      // 3. Formatted cell of the related column
      // 4. Default cell
      if (value.specificCell) {
        def.cell = value.specificCell(value)
        // console.log('Using specific cell for', key)
      } else if (value.formattedCell) {
        def.cell = ({ row }) => value.formattedCell(getNestedObject(row.original, key))
      } else if (schemaObj.formattedCell) {
        def.cell = ({ row }) => schemaObj.formattedCell(getNestedObject(row.original, key))
      } else {
        const defaultCells: AccessorKeyColumnDef<T>['cell'] = ({ row, options }) =>
          defaultCell(schemaObj, getNestedObject(row.original, key), row.original, options)

        def.cell = defaultCells
      }

      return def
    })
    .filter((x) => x !== null)
}

export function getNestedObject(obj: Record<string, any>, path: string) {
  if (!path) return null
  return path.split('.').reduce((acc, part) => acc && acc[part], obj)
}
